import { useMemo, useState } from "react";
import { isAxiosError } from "axios";
import { FileClock, LibraryBig, Scale } from "lucide-react";
import {
  createCalidadRequisitoLegal,
  createCalidadRequisitoLegalControlModificacion,
  deleteCalidadRequisitoLegal,
  deleteCalidadRequisitoLegalControlModificacion,
  updateCalidadRequisitoLegal,
  updateCalidadRequisitoLegalControlModificacion,
} from "../api/requisitos-legales";
import { useCalidadRequisitosLegales } from "../hooks/useCalidadRequisitosLegales";
import type {
  RequisitoLegalControlModificacion,
  RequisitoLegalControlModificacionPayload,
  RequisitoLegalPayload,
  RequisitoLegalRow,
} from "../types/requisitos-legales";
import {
  createEmptyRequisitoLegalPayload,
  defaultRequisitoLegalFilters,
  mapRowToPayload,
} from "../types/requisitos-legales";
import { RequisitoLegalControlModificacionFormModal } from "./RequisitoLegalControlModificacionFormModal";
import { RequisitoLegalDeleteModal } from "./RequisitoLegalDeleteModal";
import { RequisitoLegalFormModal } from "./RequisitoLegalFormModal";
import { RequisitosLegalesControlModificacionesTable } from "./RequisitosLegalesControlModificacionesTable";
import { RequisitosLegalesTable } from "./RequisitosLegalesTable";
import { Iso900114001OverviewCard } from "../../../components/shared/Iso900114001OverviewCard";
import { Iso900114001SectionHeader } from "../../../components/shared/Iso900114001SectionHeader";
import { Iso900114001Tabs } from "../../../components/shared/Iso900114001Tabs";
import { useIso900114001DebouncedValue } from "../../../components/shared/useIso900114001FilteredRequest";

type Props = {
  onBack: () => void;
};

type MainFormState =
  | { open: false }
  | { open: true; mode: "create" | "edit"; row: RequisitoLegalRow | null };

type ControlFormState =
  | { open: false }
  | {
      open: true;
      mode: "create" | "edit";
      row: RequisitoLegalControlModificacion | null;
    };

type DeleteState =
  | { open: false }
  | {
      open: true;
      kind: "requisito" | "control";
      title: string;
      description: string;
      id: number;
    };

export function CalidadRequisitosLegalesSection({ onBack }: Props) {
  const {
    rows,
    controlModificaciones,
    aspectos,
    counts,
    loading,
    error,
    reload,
  } = useCalidadRequisitosLegales(true);

  const [activeTab, setActiveTab] = useState<"requisitos" | "control">("requisitos");
  const [filters, setFilters] = useState(defaultRequisitoLegalFilters);
  const [mainForm, setMainForm] = useState<MainFormState>({ open: false });
  const [controlForm, setControlForm] = useState<ControlFormState>({ open: false });
  const [deleteState, setDeleteState] = useState<DeleteState>({ open: false });
  const [submitting, setSubmitting] = useState(false);
  const [controlSubmitting, setControlSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [controlFormError, setControlFormError] = useState<string | null>(null);
  const debouncedSearch = useIso900114001DebouncedValue(filters.search.trim(), 300).toLowerCase();

  const filteredRows = useMemo(() => {
    const search = debouncedSearch;

    return rows.filter((row) => {
      if (filters.aspecto_id && row.aspecto_id !== Number(filters.aspecto_id)) {
        return false;
      }

      if (filters.ambito && row.ambito !== filters.ambito) {
        return false;
      }

      if (filters.derogada === "si" && !row.derogada) {
        return false;
      }

      if (filters.derogada === "no" && row.derogada) {
        return false;
      }

      if (filters.cumplimiento === "si" && row.cumplimiento !== true) {
        return false;
      }

      if (filters.cumplimiento === "no" && row.cumplimiento !== false) {
        return false;
      }

      if (filters.cumplimiento === "pendiente" && row.cumplimiento !== null) {
        return false;
      }

      if (!search) {
        return true;
      }

      return [
        row.aspecto,
        row.referencia,
        row.ambito,
        row.ambito_original,
        row.derogada_original,
        row.requisito,
        row.periodicidad,
        row.registro_a_obtener,
        row.cumplimiento_original,
        row.observaciones,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(search));
    });
  }, [debouncedSearch, filters.ambito, filters.aspecto_id, filters.cumplimiento, filters.derogada, rows]);

  const mainInitialValues = useMemo(() => {
    if (!mainForm.open || !mainForm.row) {
      return createEmptyRequisitoLegalPayload();
    }

    return mapRowToPayload(mainForm.row);
  }, [mainForm]);

  const openCreateMain = () => {
    setFormError(null);
    setMainForm({ open: true, mode: "create", row: null });
  };

  const openEditMain = (row: RequisitoLegalRow) => {
    setFormError(null);
    setMainForm({ open: true, mode: "edit", row });
  };

  const handleSubmitMain = async (payload: RequisitoLegalPayload) => {
    if (!mainForm.open) {
      return;
    }

    setSubmitting(true);
    setFormError(null);

    try {
      if (mainForm.mode === "create") {
        await createCalidadRequisitoLegal(payload);
      } else if (mainForm.row) {
        await updateCalidadRequisitoLegal(mainForm.row.requisito_id, payload);
      }

      setMainForm({ open: false });
      await reload();
    } catch (requestError) {
      setFormError(
        getRequestErrorMessage(requestError, "No se pudo guardar el requisito legal."),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitControl = async (
    payload: RequisitoLegalControlModificacionPayload,
  ) => {
    if (!controlForm.open) {
      return;
    }

    setControlSubmitting(true);
    setControlFormError(null);

    try {
      if (controlForm.mode === "create") {
        await createCalidadRequisitoLegalControlModificacion(payload);
      } else if (controlForm.row) {
        await updateCalidadRequisitoLegalControlModificacion(
          controlForm.row.id,
          payload,
        );
      }

      setControlForm({ open: false });
      await reload();
    } catch (requestError) {
      setControlFormError(
        getRequestErrorMessage(
          requestError,
          "No se pudo guardar la modificación del documento.",
        ),
      );
    } finally {
      setControlSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteState.open) {
      return;
    }

    setDeleting(true);

    try {
      if (deleteState.kind === "requisito") {
        await deleteCalidadRequisitoLegal(deleteState.id);
      } else {
        await deleteCalidadRequisitoLegalControlModificacion(deleteState.id);
      }

      setDeleteState({ open: false });
      await reload();
    } finally {
      setDeleting(false);
    }
  };

  return (
    <section className="calidad-section reqleg-section">
      <Iso900114001SectionHeader
        eyebrow="Control normativo"
        title="Requisitos Legales"
        description="Gestión de referencias legales, requisitos asociados, periodicidad, registros a obtener y cumplimiento."
        onBack={onBack}
        onRefresh={() => {
          void reload();
        }}
        refreshing={loading}
      />

      <div className="iso9001-overview-grid">
        <Iso900114001OverviewCard icon={Scale} label="Aspectos" value={loading ? "..." : counts.aspectos} />
        <Iso900114001OverviewCard icon={LibraryBig} label="Referencias" value={loading ? "..." : counts.referencias} />
        <Iso900114001OverviewCard icon={FileClock} label="Requisitos" value={loading ? "..." : counts.requisitos} />
      </div>

      <Iso900114001Tabs
        tabs={[
          { id: "requisitos", label: "Requisitos legales", count: filteredRows.length },
          {
            id: "control",
            label: "Control documental",
            count: controlModificaciones.length,
          },
        ]}
        activeTab={activeTab}
        onChange={setActiveTab}
        ariaLabel="Tablas de requisitos legales"
      />

      {activeTab === "control" ? (
        <RequisitosLegalesControlModificacionesTable
          rows={controlModificaciones}
          loading={loading}
          error={error}
          onRetry={() => {
            void reload();
          }}
          onCreate={() => {
            setControlFormError(null);
            setControlForm({ open: true, mode: "create", row: null });
          }}
          onEdit={(row) => {
            setControlFormError(null);
            setControlForm({ open: true, mode: "edit", row });
          }}
          onDelete={(row) =>
            setDeleteState({
              open: true,
              kind: "control",
              id: row.id,
              title: "Eliminar modificación",
              description: `Se eliminará la modificación registrada en ${row.fecha}.`,
            })
          }
        />
      ) : (
        <RequisitosLegalesTable
          rows={filteredRows}
          aspectos={aspectos}
          loading={loading}
          error={error}
          filters={filters}
          onFiltersChange={(patch) =>
            setFilters((current) => ({ ...current, ...patch }))
          }
          onResetFilters={() => setFilters(defaultRequisitoLegalFilters)}
          onRetry={() => {
            void reload();
          }}
          onCreate={openCreateMain}
          onEdit={openEditMain}
          onDelete={(row) =>
            setDeleteState({
              open: true,
              kind: "requisito",
              id: row.requisito_id,
              title: "Eliminar requisito legal",
              description: `Se eliminará el requisito seleccionado de la referencia ${row.referencia}.`,
            })
          }
        />
      )}

      <RequisitoLegalFormModal
        open={mainForm.open}
        mode={mainForm.open ? mainForm.mode : "create"}
        aspectos={aspectos}
        rows={rows}
        initialValues={mainInitialValues}
        submitting={submitting}
        error={formError}
        onClose={() => setMainForm({ open: false })}
        onSubmit={handleSubmitMain}
      />

      <RequisitoLegalControlModificacionFormModal
        open={controlForm.open}
        mode={controlForm.open ? controlForm.mode : "create"}
        initialValues={controlForm.open ? controlForm.row : null}
        submitting={controlSubmitting}
        error={controlFormError}
        onClose={() => setControlForm({ open: false })}
        onSubmit={handleSubmitControl}
      />

      <RequisitoLegalDeleteModal
        open={deleteState.open}
        title={deleteState.open ? deleteState.title : ""}
        description={deleteState.open ? deleteState.description : ""}
        deleting={deleting}
        onClose={() => setDeleteState({ open: false })}
        onConfirm={handleDelete}
      />
    </section>
  );
}


function getRequestErrorMessage(error: unknown, fallback: string) {
  if (!isAxiosError(error)) {
    return fallback;
  }

  const fieldErrors = error.response?.data?.errors;
  const firstError =
    fieldErrors && typeof fieldErrors === "object"
      ? Object.values(fieldErrors).find(Array.isArray)?.[0]
      : null;

  if (typeof firstError === "string") {
    return firstError;
  }

  const message = error.response?.data?.message;

  return typeof message === "string" ? message : fallback;
}
